import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withRouter } from 'react-router-dom';
import { withTracker } from 'meteor/react-meteor-data';
import { isEmpty, isNil } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import ProjectsCollection from '../../../api/Projects/Projects';

import { isGeoMotionVideo } from '../../../modules/checkDeployEnv';
import { exportTextToLocalFile, importFromProjectFile } from '../../../modules/export-import-files';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import Loading from '../../components/Loading';
import TableList from '../../components/TableList';

const PER_PAGE = 15;

class Projects extends Component {
  state = {
    isTrashOpen: false,
    isCompleteItemHidden: false
  };

  closeTrash = () => this.setState({ isTrashOpen: false });

  handleExport = async projectId => {
    try {
      const exportItem = await methodPromise('projects.export', projectId);
      exportTextToLocalFile(
        `${exportItem.project.name}.json`,
        JSON.stringify(exportItem, null, '  ')
      );
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleHardRemove = async projectId => {
    try {
      const deletedNumbers = await methodPromise('projects.hardDelete', projectId);
      Bert.alert(
        `Project deleted! ${deletedNumbers.deletedFiles} files and ${deletedNumbers.deletedFolders} folders`,
        'danger'
      );
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleImport = async file => {
    try {
      await importFromProjectFile(file);
      Bert.alert('Project Imported!', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleRestore = async projectId => {
    try {
      await methodPromise('projects.restore', projectId);
      Bert.alert('Project Restored!', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleSoftRemove = async projectId => {
    try {
      await methodPromise('projects.softDelete', projectId);
      Bert.alert('Project moved to Trash!', 'warning');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  openTrash = () => this.setState({ isTrashOpen: true });

  toggleDisplayCompleted = () =>
    this.setState(prevState => ({ isCompleteItemHidden: !prevState.isCompleteItemHidden }));

  toggleDone = async (projectId, done) => {
    try {
      await methodPromise('projects.setDone', projectId, !done);
      if (!done) Bert.alert('Good Job', 'success');
      else Bert.alert('Keep up the work', 'info');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  render() {
    const {
      completeCount,
      currentPage,
      deletedCount,
      deletedProjects,
      deployConfig,
      deployEnv,
      history,
      incompleteCount,
      isBasic,
      isFree,
      isPremium,
      loading,
      match,
      projectList,
      searching,
      sort,
      totalCount,
      unfilteredCount,
      changePage,
      handleSearch,
      handleSort
    } = this.props;
    const { isTrashOpen, isCompleteItemHidden } = this.state;
    const limitBasic =
      !isEmpty(deployConfig) && isBasic ? deployConfig.Roles.Basic.ProjectLimit : 0;
    const limitFree = !isEmpty(deployConfig) && isFree ? deployConfig.Roles.Free.ProjectLimit : 0;
    const limit = limitBasic >= limitFree ? limitBasic : limitFree;
    const isFreeUserAtLimit = (isBasic || isFree) && totalCount + deletedCount >= limit;

    let filteredCount;
    if (isCompleteItemHidden) {
      filteredCount = unfilteredCount === incompleteCount ? '' : unfilteredCount - incompleteCount;
    } else {
      filteredCount = unfilteredCount === totalCount ? '' : unfilteredCount - totalCount;
    }

    const tableHeaders = [
      {
        count: isCompleteItemHidden ? incompleteCount : totalCount,
        filteredCount,
        name: 'Projects',
        sortValue: 'name'
      },
      {
        isHiddenOnXS: true,
        name: 'Last Updated',
        sortValue: 'updatedAt'
      },
      {
        isHiddenOnXS: true,
        name: 'Created',
        sortValue: 'createdAt'
      },
      {
        isButtonColumn: true
      },
      {
        isButtonColumn: true,
        name: 'Completed'
      }
    ];
    if (!isGeoMotionVideo(deployEnv))
      tableHeaders.splice(tableHeaders.length - 1, 0, { isButtonColumn: true });

    const newItems = [
      {
        eventKey: 1,
        isFreeUserAtLimit,
        itemName: 'projects',
        newItemRoute: `${match.url}/new`
      }
    ];
    const trashProps = {
      deletedCount,
      deletedItems: deletedProjects,
      isTrashOpen,
      closeTrash: this.closeTrash,
      deleteItem: this.handleHardRemove,
      restoreItem: this.handleRestore
    };
    const tablePageHeaderProps = {
      completeItemsCount: completeCount,
      hasImportButton: !isGeoMotionVideo(deployEnv),
      hasSearchInput: !isGeoMotionVideo(deployEnv),
      hasToggleHideCompletedButton: true,
      isCompleteItemHidden,
      isPremium,
      newItems,
      searching,
      handleImport: this.handleImport,
      handleSearch,
      toggleDisplayCompleted: this.toggleDisplayCompleted
    };
    const tableHeaderRowProps = {
      hasTrash: true,
      sort,
      tableHeaders,
      handleSort,
      openTrash: this.openTrash
    };
    const tableContentProps = {
      completedColumn: true,
      exportButton: !isGeoMotionVideo(deployEnv),
      history,
      isCompleteItemHidden,
      isTableEmpty: projectList.length === 0,
      itemIdentifier: 'id',
      itemName: 'projects',
      items: projectList,
      loading,
      match,
      numberOfColumns: isGeoMotionVideo(deployEnv) ? 6 : 7,
      openNewTabButton: true,
      completeItem: this.toggleDone,
      exportItem: this.handleExport,
      softDeleteItem: this.handleSoftRemove
    };
    const tablePaginationProps = {
      currentPage,
      itemsPerPage: PER_PAGE,
      totalCount,
      changePage
    };

    if (loading) return <Loading />;
    return (
      <TableList
        childProps={{
          trashProps,
          tablePageHeaderProps,
          tableHeaderRowProps,
          tableContentProps,
          tablePaginationProps
        }}
        isFreeUserAtLimit={isFreeUserAtLimit}
        isPaginationOn
        isFree={isFree}
      />
    );
  }
}

Projects.defaultProps = {
  completeCount: 0,
  deletedCount: 0,
  deletedProjects: [],
  incompleteCount: 0,
  loading: true,
  projectList: [],
  totalCount: 0,
  unfilteredCount: 0
};

Projects.propTypes = {
  completeCount: PropTypes.number,
  currentPage: PropTypes.number.isRequired,
  deletedCount: PropTypes.number,
  deletedProjects: PropTypes.array,
  deployEnv: PropTypes.string.isRequired,
  deployConfig: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  incompleteCount: PropTypes.number,
  isBasic: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  isPremium: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  match: PropTypes.object.isRequired,
  projectList: PropTypes.array,
  searching: PropTypes.bool.isRequired,
  sort: PropTypes.object.isRequired,
  totalCount: PropTypes.number,
  unfilteredCount: PropTypes.number,
  changePage: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired
};

export default withRouter(
  withTracker(({ searchValue, currentPage, sort }) => {
    const skip = currentPage * PER_PAGE - PER_PAGE;

    const projectQuery = { deleted: { $eq: 'no' } };

    const searchRegex = !isEmpty(searchValue) ? new RegExp(searchValue, 'i') : null;
    if (!isNil(searchRegex)) projectQuery.name = searchRegex;

    const sortOptions = { limit: PER_PAGE, skip, sort };
    const projectsSub = Meteor.subscribe('projects');

    return {
      completeCount: ProjectsCollection.find({ ...projectQuery, done: { $eq: true } }).count(),
      deletedCount: ProjectsCollection.find({ deleted: { $ne: 'no' } }).count(),
      deletedProjects: ProjectsCollection.find({ deleted: { $ne: 'no' } }).fetch(),
      incompleteCount: ProjectsCollection.find({ ...projectQuery, done: { $eq: false } }).count(),
      loading: !projectsSub.ready(),
      projectList: ProjectsCollection.find(projectQuery, sortOptions).fetch(),
      totalCount: ProjectsCollection.find(projectQuery).count(),
      unfilteredCount: ProjectsCollection.find({ deleted: { $eq: 'no' } }).count()
    };
  })(Projects)
);
